import { createSlice } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'
import { cleanDuplicates } from '../../../utils/data';
import { S10Response } from '../../../interfaces/auth.interface';


export interface ProfitCoins {
    CodMoneda: string,
    Descripcion: string,
    Simbolo: string,
}

export interface ProfitCompany {
    CodEmpresa: string,
    Descripcion: string,
}

export interface ProfitProject {
    CodProyecto: string,
    Descripcion: string,
}

export interface ProfitPeriod {
    CodPeriodo: string,
    Descripcion: string,
}

export interface ProfitPC {
    CodPC: string,
    CodProyecto: string,
    Descripcion: string,
}

export interface ProfitData {
    LucroBasePEN: number;
    LucroBaseUSD: number;
    LucroMetaPEN: number;
    LucroMetaUSD: number;
    LucroRealPEN: number;
    LucroRealUSD: number;
    MargenBasePEN: number;
    MargenBaseUSD: number;
    MargenMetaPEN: number;
    MargenMetaUSD: number;
    MargenRealPEN: number;
    MargenRealUSD: number;
    Periodo: string;
}

export interface ProfitSlice {
    coins: ProfitCoins[],
    coinsParts: number[],
    loadingCoins: boolean,
    companies: ProfitCompany[],
    companiesParts: number[],
    loadingCompanies: boolean,
    projects: ProfitProject[],
    projectsParts: number[],
    loadingProjects: boolean,
    periods: ProfitPeriod[],
    periodsParts: number[],
    loadingPeriods: boolean,
    profit: ProfitData[],
    profitParts: number[],
    loadingProfit: boolean,
    pc: ProfitPC[],
    pcParts: number[],
    loadingPC: boolean,
}

const initialState: ProfitSlice = {
    coins: [],
    coinsParts: [],
    loadingCoins: false,
    companies: [],
    companiesParts: [],
    loadingCompanies: false,
    projects: [],
    projectsParts: [],
    loadingProjects: false,
    periods: [],
    periodsParts: [],
    loadingPeriods: false,
    profit: [],
    profitParts: [],
    loadingProfit: false,
    pc: [],
    pcParts: [],
    loadingPC: false,
}

export const profitSlice = createSlice({
    name: 'profit',
    initialState,
    reducers: {
        setLoadingCoins: (state, {payload}: PayloadAction<boolean>) => {
            if (payload) {
                state.coins = [];
                state.coinsParts = [];
            }
            state.loadingCoins = payload;
        },
        setProfitCoinsData: (state, {payload}: PayloadAction<S10Response<ProfitCoins[]>>) => {
            if (state.coinsParts.includes(payload.part)) {
                return;
            }
            state.coins = cleanDuplicates([
                ...state.coins,
                ...payload.data,
            ], 'CodMoneda') ;
            // state.coins = [
            //     {...payload.data[0], Simbolo: 'PEN'},
            //     {...payload.data[1], Simbolo: 'USD'},
            // ]
            state.coinsParts = [...state.coinsParts, payload.part];
            state.loadingCoins = state.coinsParts.length !== payload.count;
        },
        setLoadingCompanies: (state, {payload}: PayloadAction<boolean>) => {
            state.loadingCompanies = payload;
            if (payload) {
                state.companies = [];
                state.companiesParts = [];
                state.periods = [];
                state.periodsParts = [];
                state.projects = [];
                state.projectsParts = [];
                state.pc = [];
                state.pcParts = [];

                state.profit = [];
                state.profitParts = [];
            }
        },
        setProfitCompaniesData: (state, {payload}: PayloadAction<S10Response<ProfitCompany[]>>) => {
            if (state.companiesParts.includes(payload.part)) {
                return;
            }
            state.companies = cleanDuplicates([
                ...state.companies,
                ...payload.data,
            ], 'CodEmpresa') ;
            state.companiesParts = [...state.companiesParts, payload.part];
            state.loadingCompanies = state.companiesParts.length !== payload.count;
        },
        setLoadingPeriods: (state, {payload}: PayloadAction<boolean>) => {
            state.loadingPeriods = payload;
            if (payload) {
                state.periods = [];
                state.periodsParts = [];
            }
        },
        setPeriodsData: (state, {payload}: PayloadAction<S10Response<ProfitPeriod[]>>) => {
            if (state.periodsParts.includes(payload.part)) {
                return;
            }
            state.periods = cleanDuplicates([
                ...state.periods,
                ...payload.data,
            ], 'CodPeriodo')
                .map(d => ({...d, Anio: d.CodPeriodo.slice(0, 4)}));
            state.periodsParts = [...state.periodsParts, payload.part];
            state.loadingPeriods = state.periodsParts.length !== payload.count;
        },
        setLoadingProjects: (state, {payload}: PayloadAction<boolean>) => {
            state.loadingProjects = payload;
            if (payload) {
                state.projects = [];
                state.projectsParts = [];
                state.pc = [];
                state.pcParts = [];
            }
        },
        setProfitProjectsData: (state, {payload}: PayloadAction<S10Response<ProfitProject[]>>) => {
            if (state.projectsParts.includes(payload.part)) {
                return;
            }
            state.projects = cleanDuplicates([
                ...state.projects,
                ...payload.data,
            ], 'CodProyecto') ;
            state.projectsParts = [...state.projectsParts, payload.part];
            state.loadingProjects = state.projectsParts.length !== payload.count;
        },
        setLoadingPC: (state, {payload}: PayloadAction<boolean>) => {
            state.loadingPC = payload;
            if (payload) {
                state.pc = [];
                state.pcParts = [];
            }
        },
        setPCData: (state, {payload}: PayloadAction<S10Response<ProfitPC[]>>) => {
            if (state.pcParts.includes(payload.part)) {
                return;
            }
            state.pc = cleanDuplicates([
                ...state.pc,
                ...payload.data,
            ], 'CodPC');
            state.pcParts = [...state.pcParts, payload.part];
            state.loadingPC = state.pcParts.length !== payload.count;
        },
        setLoadingProfit: (state, {payload}: PayloadAction<boolean>) => {
            state.loadingProfit = payload;
            if (payload) {
                state.profit = [];
                state.profitParts = [];
            }
        },
        setProfitData: (state, {payload}: PayloadAction<S10Response<ProfitData[]>>) => {
            if (state.profitParts.includes(payload.part)) {
                return;
            }
            state.profit = cleanDuplicates([
                ...state.profit,
                ...payload.data,
            ], 'Periodo');
            console.log('profit data updated', payload.data)
            state.profitParts = [...state.profitParts, payload.part];
            state.loadingProfit = state.profitParts.length !== payload.count;
        },
    }
})


export const {
    setLoadingCoins,
    setProfitCoinsData,
    setProfitCompaniesData,
    setLoadingCompanies,
    setProfitProjectsData,
    setLoadingProjects,
    setProfitData,
    setLoadingProfit,
    setLoadingPC,
    setPCData,
    setLoadingPeriods,
    setPeriodsData,
} = profitSlice.actions
